import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type Locale = "en" | "fr";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
};

const STORAGE_KEY = "studio_locale";

const translations: Record<Locale, Record<string, string>> = {
  en: {
    "nav.agora": "Agora",
    "nav.atlas": "Atlas",
    "nav.foundry": "Foundry",
    "nav.forum": "Forum",
    "nav.jarvis": "Jarvis",
    "nav.jarvisV2": "Jarvis V2 (Beta)",
    "nav.governance": "Governance",
    "atlas.tabs.project": "Project",
    "atlas.tabs.firstReview": "1st Review",
    "atlas.tabs.finalReview": "Final Review",
    "atlas.tabs.scoring": "Scoring",
    "atlas.tabs.applicationsList": "Applications List",
    "atlas.tabs.roadmap": "Roadmap",
    "atlas.subTabs.applications": "Applications",
    "atlas.subTabs.applicationDetails": "Application Details",
    "atlas.subTabs.finalReview": "Final Review",
    "atlas.progress.firstReview": "1st Review",
    "atlas.progress.finalReview": "Final Review",
  },
  fr: {
    "nav.agora": "Agora",
    "nav.atlas": "Atlas",
    "nav.foundry": "Foundry",
    "nav.forum": "Forum",
    "nav.jarvis": "Jarvis",
    "nav.jarvisV2": "Jarvis V2 (Beta)",
    "nav.governance": "Governance",
    "atlas.tabs.project": "Projet",
    "atlas.tabs.firstReview": "1re Revue",
    "atlas.tabs.finalReview": "Revue Finale",
    "atlas.tabs.scoring": "Scoring",
    "atlas.tabs.applicationsList": "Liste des applications",
    "atlas.tabs.roadmap": "Roadmap",
    "atlas.subTabs.applications": "Applications",
    "atlas.subTabs.applicationDetails": "Détails application",
    "atlas.subTabs.finalReview": "Revue Finale",
    "atlas.progress.firstReview": "1re Revue",
    "atlas.progress.finalReview": "Revue Finale",
  },
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function getInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "fr" || stored === "en" ? stored : "en";
}

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, nextLocale);
    }
  }, []);

  const t = useCallback(
    (key: string) => translations[locale]?.[key] ?? translations.en[key] ?? key,
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}
